import {
  ACTION,
  CONFIG_OPTION,
  FORBIDDEN,
  FUNCTION,
  OPTIONAL,
  REQUIRES,
  STRING,
} from './constants';
import { assert } from '@ember/debug';
import { doesNotExist, exists, parentExists, parseArgumentName } from './helpers';
import { getOwner } from '@ember/application';

/**
 * getRouteInfo retrieves the current route URL and name from the router service
 * for inclusion in validation error messages
 * @param {object} componentInstance the component instance to look up the owner from
 * @returns {string} formatted route info string, or empty string if unavailable
 */
function getRouteInfo(componentInstance) {
  try {
    let owner = getOwner(componentInstance);
    let router = owner?.lookup('service:router');

    if (router?.currentURL) {
      return `\n📍 Rendered at: ${router.currentURL} (route: ${router.currentRouteName})`;
    }
  } catch {
    // silently fail if router isn't available (e.g., in unit tests)
  }

  return '';
}

/**
 * buildDebugHint constructs a debug hint for finding the source of a validation error
 * @param {string} component the component name
 * @param {string} name the argument name
 * @returns {string} the debug hint string
 */
function buildDebugHint(component, name) {
  return `\n\n💡 Debug: Search for <${component.replace(/::/g, '/')} @${name}=...> in templates to find the source.`;
}

/**
 * selectConfigOption verifies that the selected value is present in the set of options
 * provided. if it is not, then a fallback value is returned
 * @param {Array<string>} options
 * @param {string} selected
 * @param {string} fallback
 * @returns {string}
 */
export function selectConfigOption(options, selected, fallback) {
  return options.find(option => option === selected) || fallback;
}

/**
 * buildConfigOptionData constructs validation data for config option validations
 * @param {string} name the argument name
 * @param {string} component the component name
 * @param {object} item the validation item
 * @param {string} routeInfo the formatted route info string
 * @returns {object} the config option validation data
 */
function buildConfigOptionData({ name, component, item, routeInfo }) {
  return {
    name,
    options: item.options,
    routeInfo,
    debugHint: buildDebugHint(component, name),
  };
}

/**
 * Normalizes the when value to always be an array or undefined
 * @param {*} when the when value to normalize
 * @returns {Array|undefined} normalized when array or undefined
 */
function normalizeWhenValue(when) {
  if (when === undefined) return undefined;
  return Array.isArray(when) ? when : [when];
}

/**
 * Builds the when clause for the forbidden message
 * @param {Array|undefined} normalizedWhen the normalized when array
 * @returns {string} the when clause for the message
 */
function buildWhenClause(normalizedWhen) {
  if (normalizedWhen === undefined) return ' is provided';
  return ` is ${normalizedWhen.map(v => JSON.stringify(v)).join(' or ')}`;
}

/**
 * buildForbiddenData constructs validation data for forbidden argument validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {object} item the validation item
 * @param {object} componentInstance the component instance
 * @returns {object} the forbidden validation data
 */
function buildForbiddenData({ name, args, component, item, componentInstance, routeInfo }) {
  const normalizedWhen = normalizeWhenValue(item.when);
  const whenClause = buildWhenClause(normalizedWhen);
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';
  let debugHint = buildDebugHint(component, name);

  let forbiddenData = {
    message: `@${name} argument of ${component} is not allowed when @${item.forbiddingArg}${whenClause} because ${item.reason}${instanceId}${routeInfo}${debugHint}`,
  };

  // ensure legacy validations without `forbiddingArg` are still supported
  if (exists(item.forbiddingArg)) {
    forbiddenData.forbiddingArgValue = parseArgumentName(args, item.forbiddingArg);
  }

  if (normalizedWhen !== undefined) {
    forbiddenData.when = normalizedWhen;
  }

  return forbiddenData;
}

/**
 * buildRequiresData constructs validation data for required dependency validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {object} item the validation item
 * @param {object} componentInstance the component instance to check for property values
 * @returns {object} the requires validation data
 */
function buildRequiresData({ name, args, component, item, componentInstance, routeInfo }) {
  const normalizedWhen = normalizeWhenValue(item.when);
  const whenClause = buildWhenClause(normalizedWhen);
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';
  let debugHint = buildDebugHint(component, item.name);

  let requiresData = {
    message: `@${item.name} argument of ${component} is required when @${name}${whenClause} because ${item.reason}${instanceId}${routeInfo}${debugHint}`,
    requiringArgValue: parseArgumentName(args, name),
    // Check the component instance property (getter) instead of just args for the required value
    // This allows defaults from metadata to be recognized by validation
    requiredArgValue: parseArgumentName(args, item.name) ?? componentInstance?.[item.name],
  };

  if (normalizedWhen !== undefined) {
    requiresData.when = normalizedWhen;
  }

  return requiresData;
}

/**
 * buildTypeData constructs validation data for type validations
 * @param {string} name the argument name
 * @param {object} args the arguments object
 * @param {string} component the component name
 * @param {string} listName the validation list name
 * @param {object} list the validation list
 * @param {object} componentInstance the component instance
 * @returns {object} the type validation data
 */
function buildTypeData({ name, args, component, listName, list, componentInstance, routeInfo }) {
  let { type } = list;
  let messageBase = `argument of ${component} must have a type of "${type}"${listName === OPTIONAL ? ' or be empty' : ''}`;
  let instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';
  let debugHint = buildDebugHint(component, name);

  return {
    message: `@${name} ${messageBase}. Received ${describeValue(args[name])}${instanceId}${routeInfo}${debugHint}`,
    type: discoverType(type),
  };
}

/**
 * describeValue renders a received value for a validation message.
 *
 * The message is composed before the value is known to be invalid, so a *valid* argument must not
 * be able to break validation. Template values make that a real risk: the `hash` helper builds
 * null-prototype objects, which have no `toString` and throw on implicit string coercion.
 * @param {*} value the received argument value
 * @returns {string} a display string for the value
 */
function describeValue(value) {
  try {
    return `${value}`;
  } catch {
    // Reached by null-prototype objects, symbols, and anything with a throwing `toString`.
    try {
      return JSON.stringify(value) ?? `[${typeof value}]`;
    } catch {
      // JSON.stringify throws on circular structures.
      return `[unprintable ${typeof value}]`;
    }
  }
}

/** discoverType determines the effective type for validation
 * @param {string} type the provided type
 * @returns {string} the effective type for validation
 */
function discoverType(type) {
  // ACTION is validated as FUNCTION.
  // COMPONENT keeps its own type so hasCorrectType can also accept curried component values.
  return type === ACTION ? FUNCTION : type;
}

/**
 * getValidationData constructs the validation data object for a given argument
 * @param {Array<object>} list the validation list
 * @param {string} listName the name of the validation list
 * @param {object} args the arguments object
 * @param {string} component the name of the component
 * @param {object|string} item the item to validate
 * @param {object} componentInstance the component instance
 * @param {string} routeInfo the formatted route info string
 * @returns {object} the constructed validation data object
 */
function getValidationData({
  list,
  listName,
  args,
  component,
  item,
  componentInstance,
  routeInfo,
}) {
  let name = typeof item === STRING ? item : item.name;
  let validationData = { argument: parseArgumentName(args, name), component };
  let additionalData;

  if (listName === FORBIDDEN) {
    additionalData = buildForbiddenData({
      name,
      args,
      component,
      item,
      componentInstance,
      routeInfo,
    });
  } else if (listName === REQUIRES) {
    additionalData = buildRequiresData({
      name: item.requiringArg,
      args,
      component,
      item,
      componentInstance,
      routeInfo,
    });
  } else if (list.type === CONFIG_OPTION) {
    additionalData = buildConfigOptionData({ name, component, item, routeInfo });
  } else {
    additionalData = buildTypeData({
      name,
      args,
      component,
      listName,
      list,
      componentInstance,
      routeInfo,
    });
  }

  return Object.assign(validationData, additionalData);
}

/**
 * validateArgumentList traverses the supplied argumentList and runs all applicable validations against
 * the args object, ensuring that the values in args are valid
 * @param {object} param0 with attributes args {object}, argumentList {array}, component {string}, and componentInstance {object}
 */
export function validateArgumentList({ args = {}, argumentList, component, componentInstance }) {
  // can't properly compose validation messages without component
  // this should be a _very_ rare case, as `validateArguments` falls back to `constructor.name`
  // when no componentName is provided
  assert(
    'component name must be present to properly compose error messages while validating components',
    typeof component === STRING && component.length > 0
  );
  // this is to ensure the `arguments` array is properly added to the validating component
  assert(
    `argumentList must be an array to properly validate arguments for ${component}`,
    Array.isArray(argumentList)
  );

  // compute route info once per component validation pass
  let routeInfo = getRouteInfo(componentInstance);

  argumentList.forEach(list => {
    // iterate over the list of validations and use the list name (forbidden, optional, required)
    // to determine the validation to use for items in the list
    assert(`argumentList of ${component} should have validations defined`, !!list.validations);
    assert(`argumentList of ${component} should have assertions defined`, !!list.assertions);

    Object.keys(list.validations).forEach(listName => {
      let validationsList = list.validations[listName];

      if (doesNotExist(validationsList)) return;

      validationsList.forEach(item => {
        let name = typeof item === STRING ? item : item.name;
        if (!parentExists(args, name)) return;

        list.assertions[listName]?.(
          getValidationData({ list, listName, args, component, item, componentInstance, routeInfo })
        );
      });
    });
  });
}

export default {
  selectConfigOption,
  validateArgumentList,
};
